// components/Careers.tsx
"use client";

import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  alpha,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  RocketLaunch,
  Groups,
  School,
  EmojiObjects,
  TrendingUp,
  Send,
  Close,
  LocationOn,
  Schedule,
  BusinessCenter,
  Check,
} from "@mui/icons-material";
import { theme } from "@/styles/theme";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "@/lib/firebase";
import JsonLd from "./JsonLd";

// Types for job vacancies
type JobType = "Full-time" | "Part-time" | "Contract" | "Remote";
type JobCategory = "Development" | "Design" | "Operations" | "Marketing";

interface Job {
  id: string;
  title: string;
  department: string;
  type: JobType;
  location: string;
  category: JobCategory;
  description: React.ReactNode;
  desc: string[];
  descPost: string;
  responsibilities: string[];
  requirements: string[];
  benefits: string[];
  applicationLink: string;
  postedDate: string;
  validThrough: string; // Add this for schema
  isRemote: boolean;
  isActive: boolean;
  salary?: {
    minValue: number;
    maxValue: number;
    currency: string;
    unitText: string;
  };
}

const benefits = [
  {
    icon: <RocketLaunch sx={{ fontSize: 32 }} />,
    title: "Real Impact",
    description:
      "Work on projects that empower SMEs, startups, and communities.",
  },
  {
    icon: <Groups sx={{ fontSize: 32 }} />,
    title: "Collaboration First",
    description: "Be part of a team that values creativity and shared success.",
  },
  {
    icon: <School sx={{ fontSize: 32 }} />,
    title: "Continuous Growth",
    description: "Access mentorship, training, and real-world challenges.",
  },
  {
    icon: <EmojiObjects sx={{ fontSize: 32 }} />,
    title: "Innovator's Spirit",
    description: "We welcome bold ideas and help bring them to life.",
  },
];

// Job Card Component (same as before)
const JobCard: React.FC<{ job: Job; onViewDetails: (job: Job) => void }> = ({
  job,
  onViewDetails,
}) => (
  <Card
    sx={{
      p: 4,
      height: "100%",
      transition: "all 0.3s ease",
      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      cursor: "pointer",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.15)}`,
        borderColor: alpha(theme.palette.primary.main, 0.3),
      },
    }}
    onClick={() => onViewDetails(job)}
  >
    {/* Job Card Content - same as your existing code */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        mb: 2,
      }}
    >
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, color: theme.palette.action.active }}
      >
        {job.title}
      </Typography>
      <Chip
        label={job.type}
        size="small"
        sx={{
          backgroundColor: alpha(theme.palette.secondary.main, 0.1),
          color: theme.palette.secondary.main,
          fontWeight: 500,
        }}
      />
    </Box>

    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <BusinessCenter sx={{ fontSize: 18, color: "text.secondary" }} />
        <Typography variant="body2" color="text.primary">
          {job.department}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LocationOn sx={{ fontSize: 18, color: "text.secondary" }} />
        <Typography variant="body2" color="text.primary">
          {job.location}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Schedule sx={{ fontSize: 18, color: "text.secondary" }} />
        <Typography variant="body2" color="text.primary">
          Posted {new Date(job.postedDate).toLocaleDateString()}
        </Typography>
      </Box>
    </Box>

    <Typography
      variant="body1"
      color="text.primary"
      sx={{ mb: 3, lineHeight: 1.6 }}
    >
      {job.description}
    </Typography>

    <List sx={{ mb: 1, py: 0 }}>
      {job.desc.slice(0, 3).map((descList, index) => (
        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: theme.palette.primary.main,
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography
                variant="body1"
                sx={{ color: "text.primary", lineHeight: 1.5 }}
              >
                {descList}
              </Typography>
            }
          />
        </ListItem>
      ))}
    </List>

    <Typography
      variant="body1"
      color="text.primary"
      sx={{ mb: 3, lineHeight: 1.5 }}
    >
      {job.descPost}
    </Typography>

    <Button
      variant="outlined"
      fullWidth
      onClick={(e) => {
        e.stopPropagation();
        onViewDetails(job);
      }}
      sx={{
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          borderColor: theme.palette.primary.dark,
        },
      }}
    >
      View Details
    </Button>
  </Card>
);

// Job Details Modal Component (same as before)
const JobDetailsModal: React.FC<{
  job: Job | null;
  open: boolean;
  onClose: () => void;
  onApply: (job: Job) => void;
}> = ({ job, open, onClose, onApply }) => {
  if (!job) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: "background.paper",
          boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.2)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          overflow: "hidden",
        },
      }}
    >
      {/* Modal Content - same as your existing code */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "white",
          p: 3,
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "white",
            backgroundColor: alpha(theme.palette.common.white, 0.1),
            "&:hover": {
              backgroundColor: alpha(theme.palette.common.white, 0.2),
            },
          }}
        >
          <Close />
        </IconButton>

        <Box sx={{ pr: 6 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: "1.5rem", md: "2rem" },
            }}
          >
            {job.title}
          </Typography>

          <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
            {job.department} • {job.location}
          </Typography>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              label={job.type}
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.common.white, 0.2),
                color: "white",
                fontWeight: 600,
                border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
              }}
            />
            {job.isRemote && (
              <Chip
                label="Remote"
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.secondary.main, 0.9),
                  color: "white",
                  fontWeight: 600,
                }}
              />
            )}
            <Chip
              label={job.category}
              size="small"
              variant="outlined"
              sx={{
                borderColor: alpha(theme.palette.common.white, 0.5),
                color: "white",
                fontWeight: 500,
              }}
            />
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Job Overview */}
        <Box
          sx={{
            p: 4,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography
            variant="h6"
            fontSize={28}
            sx={{
              fontWeight: 600,
              mb: 2,
              color: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <BusinessCenter sx={{ fontSize: 20 }} />
            Job Overview
          </Typography>
          <Typography
            variant="body1"
            sx={{ lineHeight: 1.7, color: "text.primary", fontSize: "1.05rem" }}
          >
            {job.description}
          </Typography>

          <List sx={{ my: 2, py: 0 }}>
            {job.desc.map((descList, index) => (
              <ListItem key={index} sx={{ px: 0, py: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.primary.main,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      sx={{ color: "text.primary", lineHeight: 1.6 }}
                    >
                      {descList}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>

          <Typography
            variant="body1"
            sx={{ lineHeight: 1.7, color: "text.primary", fontSize: "1.05rem" }}
          >
            {job.descPost}
          </Typography>
        </Box>

        {/* Key Responsibilities */}
        <Box
          sx={{
            p: 4,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography
            variant="h6"
            fontSize={28}
            sx={{
              fontWeight: 600,
              mb: 3,
              color: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <RocketLaunch sx={{ fontSize: 20 }} />
            Key Responsibilities
          </Typography>
          <List sx={{ py: 0 }}>
            {job.responsibilities.map((responsibility, index) => (
              <ListItem
                key={index}
                sx={{ px: 0, py: 1, alignItems: "flex-start" }}
              >
                <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.primary.main,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      sx={{ color: "text.primary", lineHeight: 1.6 }}
                    >
                      {responsibility}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Requirements */}
        <Box
          sx={{
            p: 4,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography
            variant="h6"
            fontSize={28}
            sx={{
              fontWeight: 600,
              mb: 3,
              color: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <School sx={{ fontSize: 20 }} />
            Requirements & Qualifications
          </Typography>
          <List sx={{ py: 0 }}>
            {job.requirements.map((requirement, index) => (
              <ListItem
                key={index}
                sx={{ px: 0, py: 1, alignItems: "flex-start" }}
              >
                <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.secondary.main,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      sx={{ color: "text.primary", lineHeight: 1.6 }}
                    >
                      {requirement}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Benefits */}
        <Box sx={{ p: 4 }}>
          <Typography
            variant="h6"
            fontSize={28}
            sx={{
              fontWeight: 600,
              mb: 3,
              color: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <EmojiObjects sx={{ fontSize: 20 }} />
            What We Offer
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            {job.benefits.map((benefit, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.success.dark, 0.05),
                  border: `1px solid ${alpha(theme.palette.success.dark, 0.1)}`,
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    backgroundColor: theme.palette.success.dark,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Check sx={{ fontSize: 16, color: "white" }} />
                </Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: "text.primary" }}
                >
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          gap: 2,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            px: 4,
            py: 1.5,
            fontWeight: 600,
            borderColor: alpha(theme.palette.primary.main, 0.3),
            color: "text.primary",
            "&:hover": {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            },
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={() => onApply(job)}
          size="large"
          startIcon={<Send />}
          sx={{
            px: 4,
            py: 1.5,
            fontWeight: 600,
            fontSize: "1rem",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            },
            transition: "all 0.3s ease",
          }}
        >
          Apply for Position
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function Careers() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch jobs from Firebase
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const q = query(
          collection(db, "jobs"),
          where("isActive", "==", true),
          orderBy("postedDate", "desc")
        );

        const querySnapshot = await getDocs(q);
        const jobsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Job[];

        setJobs(jobsData);
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  // Generate JSON-LD schema
  const generateJobSchema = () => {
    const jobSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      numberOfItems: jobs.length,
      itemListElement: jobs.map((job, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "JobPosting",
          title: job.title,
          datePosted: job.postedDate,
          validThrough:
            job.validThrough || getDefaultValidThrough(job.postedDate),
          employmentType: mapEmploymentType(job.type),
          hiringOrganization: {
            "@type": "Organization",
            name: "LinXar Labs",
            sameAs: "https://linxarlabs.com",
            logo: "https://linxarlabs.com/linxar-main-logo.svg",
          },
          jobLocation: {
            "@type": "Place",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Your Company Street Address",
              addressLocality: "Accra",
              addressRegion: "Greater Accra Region",
              postalCode: "00233",
              addressCountry: "GH",
            },
          },
          baseSalary: {
            "@type": "MonetaryAmount",
            currency: "GHS",
            value: {
              "@type": "QuantitativeValue",
              minValue: job.salary?.minValue || 1000, // UPDATE: Actual salary
              maxValue: job.salary?.maxValue || 2500, // UPDATE: Actual salary
              unitText: job.salary?.unitText || "MONTH",
            },
          },
          description:
            typeof job.description === "string"
              ? job.description
              : "Career opportunity at LinXar Labs",
          responsibilities: job.responsibilities?.join(" ") || "",
          qualifications: job.requirements?.join(" ") || "",
          applicationContact: {
            "@type": "ContactPoint",
            email: "careers@linxarlabs.com",
            contactType: "HR",
          },
        },
      })),
    };

    return jobSchema;
  };

  // Helper functions
  const getDefaultValidThrough = (postedDate: string): string => {
    const date = new Date(postedDate);
    date.setDate(date.getDate() + 90);
    return date.toISOString().split("T")[0];
  };

  const mapEmploymentType = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      "Full-time": "FULL_TIME",
      "Part-time": "PART_TIME",
      Contract: "CONTRACTOR",
      Remote: "REMOTE",
    };
    return typeMap[type] || "FULL_TIME";
  };

  const handleViewDetails = (job: Job) => {
    setSelectedJob(job);
    setModalOpen(true);
  };

  const handleApply = (job: Job) => {
    window.open(job.applicationLink, "_blank");
    setModalOpen(false);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedJob(null);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading career opportunities...</Typography>
      </Container>
    );
  }

  return (
    <Box>
      <JsonLd id="careers-job-schema" schema={generateJobSchema()} />

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.1
          )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          py: 8,
          mb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ maxWidth: 800, mx: "auto" }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                fontWeight: 800,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                mb: 3,
                lineHeight: 1.2,
              }}
            >
              Grow with us. Build the future.
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{
                fontSize: { xs: "1.1rem", md: "1.25rem" },
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              Join a team that&apos;s shaping the digital landscape through
              innovation and collaboration
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Current Openings Section */}
        {jobs.length > 0 && (
          <Box sx={{ mb: 12 }}>
            <Box textAlign="center" sx={{ mb: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  mb: 2,
                }}
              >
                Current Openings
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: "auto" }}
              >
                Explore our available positions and find the perfect role for
                your skills and ambitions
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {jobs.map((job) => (
                <Grid key={job.id} size={{ xs: 12, md: 6 }}>
                  <JobCard job={job} onViewDetails={handleViewDetails} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Introduction Section */}
        <Box sx={{ mb: 12 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  fontWeight: 700,
                  mb: 3,
                  color: theme.palette.primary.main,
                }}
              >
                Build Your Future With Us
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.8,
                  color: "text.secondary",
                  mb: 3,
                }}
              >
                At LinXar Labs, we&apos;re not just building
                technology&nbsp;&mdash;&nbsp;we&apos;re building people.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.8,
                  color: "text.secondary",
                }}
              >
                Our mission is powered by passionate talents who share our drive
                to innovate, solve problems, and create meaningful impact.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  borderRadius: 4,
                  p: 4,
                  color: "white",
                  textAlign: "center",
                }}
              >
                <TrendingUp sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Your Growth Journey
                </Typography>
                <Typography>
                  Whether you&apos;re a developer, designer, strategist, or
                  dreamer, LinXar Labs offers you the opportunity to learn,
                  grow, and work on projects that matter.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Why Join Us Section */}
        <Box sx={{ mb: 12 }}>
          <Box textAlign="center" sx={{ mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", md: "2.5rem" },
                fontWeight: 700,
                color: theme.palette.primary.main,
              }}
            >
              Why Join Us?
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: "auto", mt: 2 }}
            >
              We&apos;re building more than
              software&nbsp;&mdash;&nbsp;we&apos;re building careers and
              communities
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                  sx={{
                    textAlign: "center",
                    p: 4,
                    height: "100%",
                    transition: "all 0.3s ease",
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 20px 40px ${alpha(
                        theme.palette.primary.main,
                        0.15
                      )}`,
                    },
                  }}
                >
                  <Box sx={{ color: theme.palette.primary.main, mb: 3 }}>
                    {benefit.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Future Openings Section */}
        <Box sx={{ mb: 12 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.secondary.main,
                    0.1
                  )} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                  borderRadius: 4,
                  p: 4,
                  height: "100%",
                }}
              >
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: 700, color: theme.palette.primary.main }}
                >
                  Future Openings
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "1.1rem",
                    lineHeight: 1.8,
                    color: "text.secondary",
                  }}
                >
                  We&apos;re constantly growing and expanding our team. Even if
                  you don&apos;t see the perfect role today, we encourage you to
                  get in touch and introduce yourself.
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ pl: { md: 4 } }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ fontWeight: 600, color: theme.palette.secondary.main }}
                >
                  Roles We&apos;re Building Towards:
                </Typography>
                <List>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Full-Stack Developers" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="UI/UX Designers" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Project Managers" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Business Analysts" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Digital Marketing Specialists" />
                  </ListItem>
                </List>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box
          sx={{
            textAlign: "center",
            p: 6,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Send
            sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }}
          />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Interested? Get in Touch
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 500, mx: "auto" }}
          >
            Let&apos;s explore the possibilities together. Introduce yourself
            and tell us what excites you about joining our mission.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="/contact"
            startIcon={<Send />}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              px: 4,
              py: 1.5,
              fontSize: "1.1rem",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 8px 25px ${alpha(
                  theme.palette.primary.main,
                  0.3
                )}`,
              },
              transition: "all 0.3s ease",
            }}
          >
            Introduce Yourself
          </Button>
        </Box>
      </Container>

      {/* Job Details Modal */}
      <JobDetailsModal
        job={selectedJob}
        open={modalOpen}
        onClose={handleCloseModal}
        onApply={handleApply}
      />
    </Box>
  );
}
